import { callWebUtils } from "../../../Components/CampaignMembers/helpers";
import { getLatestsReleasedChannelAdCreatives } from "../../../../../controller/campaign-creation/helpers";
import { withHttp } from "./utils";
import database from "../../..//utils/FirebaseController";
import { t } from "i18next";
import { CHANNELS_WITHOUT_CREATIVES } from "../../../common/constants/channelsWithoutCreatives";

/**
 * Get default media assets for a given set of creatives
 *
 * @param {string[]} defaultCreatives - An array of creatives
 * @returns {Promise<string[]>} Returns an array of media asset ids
 */
const getDefaultMediaAssets = async (defaultCreatives, channel, orgaId, companyId) => {
  let defaultMediaAssetIds = [];

  const context = {
    failed: () => {
      return defaultMediaAssetIds;
    },
    completed: ({ payload }) => {
      defaultMediaAssetIds = payload;
      return defaultMediaAssetIds;
    }
  };

  const initialMediaAssets = {
    text: t("We are looking for you"),
    secondaryText: t("Are you ready?"),
    CTAText: t("Apply now"),
    logo: "",
    channel,
    backgroundMedia: defaultCreatives,
    orgaId,
    companyId
  };

  await callWebUtils(context, "createMultipleMediaAssets", [initialMediaAssets]);
  return defaultMediaAssetIds;
};

/**
 * Get media asset by Ids
 *
 * @param {string[]} mediaAssetIds - An array of media asset ids
 * @returns {Promise<object[]>} Returns an array of media asset objects
 */
export const getMediaAssets = async (mediaAssetIds) => {
  let mediaAssetData = [];

  const context = {
    failed: () => {
      return mediaAssetIds;
    },
    completed: ({ payload }) => {
      mediaAssetData = payload.mediaAssets;
      return mediaAssetData;
    }
  };

  const data = { mediaAssetIds: mediaAssetIds };

  await callWebUtils(context, "getMultipleMediaAssetByIds", [data]);
  return mediaAssetData;
};

/**
 * Duplicate media assets by creating new media assets with the same data
 *
 * @param {string[]} mediaAssetIds - An array of media asset ids
 * @returns {Promise<object[]>} Returns an array with the duplicated media asset ids
 */
const duplicateMediaAssets = async (mediaAssetIds) => {
  let newMediaAssets = [];

  const context = {
    failed: () => {
      return newMediaAssets;
    },
    completed: ({ payload }) => {
      newMediaAssets = payload.duplicatedMediaAssetIds;
      return newMediaAssets;
    }
  };

  const mediaAssetsIdsToDuplicate = { mediaAssetIds: mediaAssetIds };

  await callWebUtils(context, "duplicateMultipleMediaAssets", [mediaAssetsIdsToDuplicate]);
  return newMediaAssets;
};

/**
 * Get the posts with unique media asset ids between the released and staging posts
 *
 * @param {Object} params
 * @param {Object} params.post - The post object
 * @param {Object[]} params.stagingPosts - An array of staging posts
 * @returns {Promise<Object>} Returns the post object with unique media asset ids
 */
const getPostsWithUniqueMediaAssets = async ({ post, stagingPosts }) => {
  const { mediaAssets } = post;

  if (!Array.isArray(mediaAssets)) {
    return post;
  }

  const relatedStagingPost = stagingPosts.find(
    (stagingPost) =>
      stagingPost.channel === post.channel && stagingPost.postNumber === post.postNumber
  );

  if (!relatedStagingPost) return post;

  // The media assets should be unique between the released and staging posts
  const newMediaAssets = [];
  for (let i = 0; i < mediaAssets.length; i++) {
    const mediaAssetId = mediaAssets[i];
    const stagingMediaAssetId = relatedStagingPost.mediaAssets[i];

    let newMediaAssetId = mediaAssetId;
    if (mediaAssetId && mediaAssetId === stagingMediaAssetId) {
      const duplicatedAssets = await duplicateMediaAssets([mediaAssetId]);
      newMediaAssetId = duplicatedAssets[0];
    }

    newMediaAssets.push(newMediaAssetId);
  }

  return { ...post, mediaAssets: newMediaAssets };
};

/**
 * Create media assets for creatives with null or undefined mediaAssets
 *
 * @param {Object} post - The post object
 * @returns {Promise<string[]>} Returns the array of media asset ids
 */
const createNullMediaAssets = async (post, companyId) => {
  const { creatives, mediaAssets } = post;
  const newMediaAssets = [];

  for (let i = 0; i < creatives.length; i++) {
    const mediaAssetId = mediaAssets[i];
    const creative = creatives[i];

    if (mediaAssetId) {
      newMediaAssets.push(mediaAssetId);
    } else {
      const defaultMediaAssets = await getDefaultMediaAssets(
        [creative],
        post.channel,
        post.orgaId,
        companyId
      );
      newMediaAssets.push(defaultMediaAssets[0]);
    }
  }

  return newMediaAssets;
};

/**
 * Get the preview data from the corrected posts
 *
 * @param {Object} params
 * @param {Object[]} params.finalPosts - An array of final posts (staging and released) with corrected media assets
 * @param {string[]} params.channelsToUse - An array of channels to use
 * @returns The formatted preview data
 */
const getPreviewData = ({ finalPosts, channelsToUse }) => {
  const previewData = {
    releasedVersion: 1,
    stagingVersion: 0,
    posts: {},
    channelsData: [],
    released: {
      posts: {},
      channelsData: []
    }
  };

  finalPosts.forEach(
    ({ type, channel, channelData, releasedVersion, stagingVersion, ...postData }) => {
      if (!channelsToUse.includes(channel)) {
        return;
      }

      const { posts, channelsData, released } = previewData;

      const targetPosts = type === "staging" ? posts : released.posts;
      const targetChannelsData = type === "staging" ? channelsData : released.channelsData;

      targetPosts[channel] = [
        ...(targetPosts[channel] || []),
        {
          ...postData,
          logo: postData.logo || "",
          type,
          channel // Add channel name to post
        }
      ];

      targetChannelsData[channel] = targetChannelsData[channel] || [];
      targetChannelsData[channel].push({
        ...channelData,
        url: withHttp(channelData.url),
        postNumber: postData.postNumber
      });

      if (type === "staging" && stagingVersion != null) {
        previewData.stagingVersion = stagingVersion;
      } else if (releasedVersion != null) {
        previewData.releasedVersion = releasedVersion;
      }
    }
  );

  return previewData;
};

/**
 * Transforms the old facebook logo format to the new one, placing the logo in the media asset inputProps
 *
 * @param {Array<Object>} posts - An array of post objects
 */
const transformFacebookLogos = async (posts) => {
  const facebookPostsWithLogos = posts
    .filter((post) => post.channel === "facebook")
    .filter((post) => post.logos);

  await Promise.all(
    facebookPostsWithLogos.map(async (post) => {
      const { logos } = post;

      if (post.mediaAssets && post.mediaAssets.length) {
        const mediaAssets = await getMediaAssets(post.mediaAssets);

        await Promise.all(
          logos.map(async (logo, index) => {
            const mediaAsset = mediaAssets[index];

            if (mediaAsset && logo) {
              mediaAsset.inputProps.logo = logo;
              mediaAsset.updatedDate = new Date().toISOString();
              await database.updateMediaAsset(mediaAsset.id, mediaAsset);
            }
          })
        );
      }

      await database.updateAdCreative(post.id, { logos: null });
    })
  );
};

export const dbPostsToPreviewData = async ({ posts = [], channelsToUse, companyId }) => {
  // Divide into staging and released
  const releasedPosts = posts.filter((post) => post.type === "released");
  const stagingPosts = posts.filter((post) => post.type === "staging");

  // Save media assets logos for both staging and released posts if they have values
  if (stagingPosts.length > 0) {
    await transformFacebookLogos(stagingPosts);
  }
  if (releasedPosts.length > 0) {
    await transformFacebookLogos(releasedPosts);
  }

  // Add a filter only for the latest released ones
  const latestReleasedPosts = getLatestsReleasedChannelAdCreatives({
    adCreatives: releasedPosts,
    channels: channelsToUse
  });

  // Compare released and staging posts, and create unique media assets
  const releasedPostsWithUniqueMediaAssets = await Promise.all(
    latestReleasedPosts.map(async (post) => {
      return getPostsWithUniqueMediaAssets({ post, stagingPosts });
    })
  );

  // Merge the latest released version with the staging ones
  const mergedPosts = [...releasedPostsWithUniqueMediaAssets, ...stagingPosts];

  // Create all missing media assets
  const finalPosts = await Promise.all(
    mergedPosts.map(async (post) => {
      let newMediaAssets;

      if (CHANNELS_WITHOUT_CREATIVES.includes(post.channel)) {
        return post;
      }

      if (!Array.isArray(post.mediaAssets) || post.mediaAssets.length === 0) {
        newMediaAssets = await getDefaultMediaAssets(
          post.creatives,
          post.channel,
          post.orgaId,
          companyId
        );
      } else {
        newMediaAssets = await createNullMediaAssets(post);
      }

      return { ...post, mediaAssets: newMediaAssets };
    })
  );

  const previewData = getPreviewData({ finalPosts, channelsToUse });

  return previewData;
};
